import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { computed, type ReadonlySignal } from '@preact/signals-core';

import { PropertyBase } from '../../core/view-manager/property.js';
import { SingleViewBase } from '../../core/view-manager/single-view.js';
import type { ViewManager } from '../../core/view-manager/view-manager.js';
import type { ChartViewData } from './define.js';

export const EMPTY_LABEL = '(leer)';

type SelectOption = { id: string; value: string; color: string };

/** ChartSingleView aggregates the rows of a database into chart points. */
export class ChartSingleView extends SingleViewBase<ChartViewData> {
  /**
   * Aggregated chart points: one entry per category, in option order for
   * (multi-)select properties and in order of first appearance otherwise.
   */
  readonly chartData$: ReadonlySignal<{ label: string; value: number }[]> =
    computed(() => {
      const data = this.data$.value;
      const categoryId = data?.categoryPropertyId;
      if (!categoryId) return [];
      const aggregation = data.aggregation ?? 'count';
      const valueId = aggregation === 'count' ? undefined : data.valuePropertyId;

      const categoryProperty = this.propertyGetOrCreate(categoryId);
      const type = categoryProperty.type$.value as string;
      const isSelect = type === 'select' || type === 'multi-select';
      const options = isSelect
        ? ((categoryProperty.data$.value as { options?: SelectOption[] })
            ?.options ?? [])
        : [];
      const optionName = new Map(options.map(o => [o.id, o.value]));

      const buckets = new Map<
        string,
        { count: number; sum: number; numeric: number }
      >();
      const bucketOf = (label: string) => {
        let bucket = buckets.get(label);
        if (!bucket) {
          bucket = { count: 0, sum: 0, numeric: 0 };
          buckets.set(label, bucket);
        }
        return bucket;
      };
      // Seed in option order so empty options stay visible and ordered.
      for (const option of options) bucketOf(option.value);

      for (const row of this.rows$.value) {
        const cell = this.cellGetOrCreate(row.rowId, categoryId);
        let labels: string[];
        if (isSelect) {
          const raw = cell.value$.value as string | string[] | undefined;
          const ids = Array.isArray(raw) ? raw : raw ? [raw] : [];
          labels = ids.map(id => optionName.get(id)).filter(Boolean) as string[];
        } else {
          const text = String(cell.stringValue$.value ?? '').trim();
          labels = text ? [text] : [];
        }
        if (labels.length === 0) labels = [EMPTY_LABEL];
        const amount = valueId
          ? Number(this.cellGetOrCreate(row.rowId, valueId).value$.value)
          : NaN;
        for (const label of labels) {
          const bucket = bucketOf(label);
          bucket.count += 1;
          if (Number.isFinite(amount)) {
            bucket.sum += amount;
            bucket.numeric += 1;
          }
        }
      }

      let points = [...buckets.entries()].map(([label, bucket]) => ({
        label,
        value:
          aggregation === 'count' || !valueId
            ? bucket.count
            : aggregation === 'sum'
              ? bucket.sum
              : bucket.numeric
                ? bucket.sum / bucket.numeric
                : 0,
      }));
      // Drop unused (and the synthetic empty) buckets that have no rows.
      points = points.filter(
        p => buckets.get(p.label)!.count > 0 || p.label !== EMPTY_LABEL
      );
      if (isSelect && !points.some(p => buckets.get(p.label)!.count > 0)) {
        return [];
      }
      const sort = data.sort ?? 'natural';
      if (sort === 'desc') points.sort((a, b) => b.value - a.value);
      if (sort === 'asc') points.sort((a, b) => a.value - b.value);
      return points;
    });

  /**
   * Overrides propertyGetOrCreate only if you need custom Property handling.
   * For Chart, we only group by an existing property; we do not need extra property logic.
   */
  override propertyGetOrCreate(propertyId: string): ChartProperty {
    return new ChartProperty(this, propertyId);
  }

  /** Raw property list simply mirrors all datasource properties. */
  readonly propertiesRaw$ = computed(() => {
    return this.dataSource.properties$.value.map(id =>
      this.propertyGetOrCreate(id)
    );
  });

  /** All properties are visible in Chart view. */
  readonly properties$ = computed(() => this.propertiesRaw$.value);

  /** No extra detail properties beyond the normal list. */
  readonly detailProperties$ = computed(() => this.properties$.value);

  /** Title/icon columns follow the datasource defaults. */
  readonly mainProperties$ = computed(() => ({
    titleColumn: this.propertiesRaw$.value.find(p => p.type$.value === 'title')
      ?.id,
    iconColumn: 'type',
  }));

  /** Chart view respects the datasource readonly state. */
  readonly readonly$ = computed(() => this.manager.readonly$.value);

  /**
   * The view mode string identifying this view type.
   * If the backing data is missing we still return 'chart'.
   */
  override get type(): string {
    return this.data$.value?.mode ?? 'chart';
  }

  /** Display all rows. */
  isShow(_rowId: string): boolean {
    return true;
  }

  constructor(viewManager: ViewManager, viewId: string) {
    super(viewManager, viewId);
  }
}

/** Minimal property representation for Chart view. */
export class ChartProperty extends PropertyBase {
  /** Chart view does not support hiding columns; always visible. */
  hide$ = computed(() => false);

  /** Hiding is ignored as properties are always visible. */
  override hideSet(_hide: boolean): void {
    // no-op
  }

  /**
   * Chart view doesn\'t maintain its own ordering, so move() is a noop.
   * @param _position - Unused insert position.
   */
  override move(_position: InsertToPosition): void {
    // no-op
  }

  constructor(
    readonly chartView: ChartSingleView,
    propertyId: string
  ) {
    super(chartView, propertyId);
  }
}
